import { SpeedTestResult, TestState } from '../types';

// The socket.io client is loaded from a script tag in index.html
declare const io: any;

interface SpeedTestCallbacks {
  onStateChange: (state: Extract<TestState, 'testing-ping' | 'testing-download' | 'testing-upload'>) => void;
  onPingUpdate: (ping: number, jitter: number) => void;
  onDownloadUpdate: (speed: number) => void;
  onUploadUpdate: (speed: number) => void;
  onComplete: (result: SpeedTestResult) => void;
  onError: (message: string) => void;
  onProgress: (progress: number) => void;
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
const createSpeedSmoother = (windowSize = 5) => {
  const readings: number[] = [];
  return (newSpeed: number): number => {
    readings.push(newSpeed);
    if (readings.length > windowSize) readings.shift();
    return readings.reduce((a, b) => a + b, 0) / readings.length;
  };
};

export const runSpeedTestOverWebSocket = async (callbacks: SpeedTestCallbacks, serverUrl: string, durationSeconds: number): Promise<void> => {
    const socket = io(serverUrl, { transports: ['websocket'] });

    const cleanup = () => {
        if (socket.connected) {
            socket.emit('test_stop');
            socket.disconnect();
        }
    };
    
    socket.on('connect_error', () => {
        callbacks.onError('Could not connect to the test server via WebSocket.');
        cleanup();
    });

    socket.on('connect', async () => {
        try {
            const result: SpeedTestResult = { ping: 0, jitter: 0, download: 0, upload: 0 };
            const WARMUP_MS = 2000;
            const TEST_DURATION_MS = durationSeconds * 1000;

            // 1. Ping Test
            callbacks.onStateChange('testing-ping');
            const pings: number[] = []; const PING_COUNT = 10;
            for (let i = 0; i < PING_COUNT; i++) {
                const start = performance.now();
                await new Promise<void>((resolve, reject) => {
                    const timeout = setTimeout(() => {
                        pings.push(3001); // Use a high value to indicate timeout
                        resolve();
                    }, 3000);

                    socket.emit('ping_request', {}, () => {
                        clearTimeout(timeout);
                        pings.push(performance.now() - start);
                        resolve();
                    });
                });

                const currentPing = pings[pings.length - 1];
                const jitter = pings.length > 1 ? Math.abs(currentPing - pings[pings.length - 2]) : 0;
                callbacks.onPingUpdate(Math.round(Math.min(3000, currentPing)), Math.round(jitter));
                callbacks.onProgress(((i + 1) / PING_COUNT) * 10);
                await sleep(350);
            }
            
            const successfulPings = pings.filter(p => p <= 3000);
            if (successfulPings.length < PING_COUNT / 2) {
                throw new Error("Could not establish a stable connection via WebSocket.");
            }
            
            result.ping = Math.round(successfulPings.reduce((a, b) => a + b, 0) / successfulPings.length);
            const jitters = successfulPings.slice(1).map((p, i) => Math.abs(p - successfulPings[i]));
            result.jitter = jitters.length > 0 ? Math.round(jitters.reduce((a, b) => a + b, 0) / jitters.length) : 0;
            callbacks.onPingUpdate(result.ping, result.jitter);
            await sleep(500);


            // 2. Download Test
            callbacks.onStateChange('testing-download');
            const smoothDownload = createSpeedSmoother();
            let totalLoadedBytes = 0;
            const downloadStartTime = performance.now();
            
            socket.on('download_chunk', (chunk) => {
                totalLoadedBytes += chunk.length;
                const elapsed = performance.now() - downloadStartTime;
                if (elapsed > WARMUP_MS) {
                    const speedMbps = (totalLoadedBytes * 8) / ((elapsed - WARMUP_MS) * 1000);
                    callbacks.onDownloadUpdate(parseFloat(smoothDownload(speedMbps).toFixed(2)));
                }
            });
            
            socket.emit('download_request');
            await sleep(TEST_DURATION_MS + WARMUP_MS);
            socket.emit('test_stop'); // Stop the download stream
            socket.off('download_chunk'); // Remove listener
            
            const finalElapsedMs = performance.now() - downloadStartTime;
            const finalTestDurationSeconds = (finalElapsedMs > WARMUP_MS) ? (finalElapsedMs - WARMUP_MS) / 1000 : 0;
            result.download = (finalTestDurationSeconds > 0 && totalLoadedBytes > 0) ? parseFloat(((totalLoadedBytes * 8) / (finalTestDurationSeconds * 1000 * 1000)).toFixed(2)) : 0;
            callbacks.onDownloadUpdate(result.download);
            await sleep(500);

            // 3. Upload Test
            callbacks.onStateChange('testing-upload');
            const smoothUpload = createSpeedSmoother();
            let totalUploadedBytes = 0;
            const UPLOAD_CHUNK_SIZE_BYTES = 1024 * 256; // 256 KB
            const dataChunk = new Uint8Array(UPLOAD_CHUNK_SIZE_BYTES);
            let testIsActive = true;
            let firstUploadError: Error | null = null;
            const uploadStartTime = performance.now();

            const uploadWorker = async () => {
                while (testIsActive) {
                    try {
                        await new Promise<void>((resolve, reject) => {
                           const timeout = setTimeout(() => reject(new Error("Upload acknowledgment timeout.")), 5000);
                           socket.emit('upload_chunk', dataChunk, () => {
                               clearTimeout(timeout);
                               if(testIsActive) totalUploadedBytes += UPLOAD_CHUNK_SIZE_BYTES;
                               resolve();
                           });
                        });
                    } catch (e) {
                        if (testIsActive && e instanceof Error) {
                            if (!firstUploadError) firstUploadError = e;
                            console.warn('WS upload worker error:', e.message);
                        }
                        break; // Stop this worker on the first error
                    }
                }
            };
            
            const uploadPromise = uploadWorker();

            const uploadProgressUpdater = setInterval(() => {
                if (!testIsActive) return;
                const elapsed = performance.now() - uploadStartTime;
                if (elapsed > WARMUP_MS) {
                    const speedMbps = (totalUploadedBytes * 8) / ((elapsed - WARMUP_MS) * 1000);
                    callbacks.onUploadUpdate(parseFloat(smoothUpload(speedMbps).toFixed(2)));
                }
            }, 250);

            await sleep(TEST_DURATION_MS + WARMUP_MS);
            testIsActive = false;
            clearInterval(uploadProgressUpdater);
            await uploadPromise;
            
            const finalUploadTimeMs = performance.now() - uploadStartTime;
            const finalUploadTestDurationSeconds = (finalUploadTimeMs > WARMUP_MS) ? (finalUploadTimeMs - WARMUP_MS) / 1000 : 0;
            result.upload = (finalUploadTestDurationSeconds > 0 && totalUploadedBytes > 0) ? parseFloat(((totalUploadedBytes * 8) / (finalUploadTestDurationSeconds * 1000 * 1000)).toFixed(2)) : 0;

            if (totalUploadedBytes === 0 && firstUploadError) {
                result.uploadError = firstUploadError.message;
            } else if (totalUploadedBytes === 0 && finalUploadTimeMs > WARMUP_MS) {
                result.uploadError = "Upload test transferred no data.";
            }
            
            callbacks.onUploadUpdate(result.upload);
            await sleep(500);

            callbacks.onComplete(result);
        } catch(error) {
            callbacks.onError(error instanceof Error ? error.message : "An unknown error occurred during the WebSocket test.");
        } finally {
            cleanup();
        }
    });
};